/* eslint-disable class-methods-use-this */

import {UIUtilsEjsVariables} from './UIUtilsEjsVariables.ts'
import {CA} from './analytics/Analytics.ts'
import {ClientUserState} from './ClientUserState.ts'
import {ClientI18nInitiation} from './ClientI18n.ts'
import * as Sentry from '@sentry/browser'
import {UIGameSpriteHandler} from './UIGameSpriteHandler.ts'
import {UIReferralController} from './marketing/UIReferralController.ts'
import {type UINotificationController} from './notifications/UINotificationController.ts'
import {type SocketActionController} from './socket/SocketActionController.ts'
import {StorageWrapper} from './StorageWrapper.ts'
import {UIDocument} from './UIDocument.ts'
import {UIVersionController} from './UIVersionController.ts'
import {type UIGameManager} from './ui_game/UIGameManager.ts'
import {UIImagePathController} from './UIEnums.ts'
import {ColonistMembershipProduct} from '../../../shared/store/ColonistMembershipProduct.ts'
import {ColonistCoinProduct} from '../../../shared/store/ColonistCoinProduct.ts'
import {FeedbackFormUtils} from './ui_popup/ui_popup_feedback_form/FeedbackFormUtils.ts'
import {ColonistOneTimePurchaseProduct} from '../../../shared/store/ColonistOneTimePurchaseProduct.ts'
import {UIServerNotificationController} from './ui_popup/ui_popup_server_notification/UIServerNotificationController.ts'
import {TabCommunication, TabCommunicationEvent, type TabCommunicationInterface} from './tab_communication/TabCommunication.ts'
import {GlobalClientVariables} from './GlobalClientVariables.ts'
import {ViewPopup} from './ui_popup/ViewPopup.ts'
import {delay} from '@colonist/utils'
import {type UIManagerEvents} from './ui_html/UIManagerEvents.ts'
import {GoogleOneTapLoader} from './GoogleOneTapLoader.ts'
import {HotjarLoader} from './HotjarLoader.ts'
import {ValueImpressionLoader} from './ValueImpressionLoader.ts'
import {AdinPlayLoader} from './AdinPlayLoader.ts'
import {ColonistGiftMembershipProduct} from '../../../shared/store/ColonistGiftMembershipProduct.ts'
import {UINotificationSendHelper} from './notifications/UINotificationSendHelper.ts'
import {Socket} from './socket/Socket.ts'
import './socket/UISocketMonitor.ts' // adds global startSocketMonitor
import {type AjaxDataTypes} from '../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../shared/entity_datas/EntityDatas.ts'
import {UIUrlController} from './ui_html/UIUrlController.ts'
import type {BrowserOptions} from '@sentry/browser'
import {initTooltips} from './ui_utils/TooltipUtils.ts'
import {AbortError} from '@colonist/types'
import {logError} from './ui_utils/Utils.ts'
import {UITurnstile} from './network/UITurnstile.ts'
import {AudioManager} from '@colonist/audio'
import {ClientPlatformRedirection} from './ClientPlatformRedirection.ts'

export let versionController: UIVersionController | undefined
export let uiGameManager: UIGameManager
export let socket: Socket
export let notificationController: UINotificationController
export let tabCommunication: TabCommunicationInterface
export let globalClientVariables: GlobalClientVariables
export let uiManagerEvents: UIManagerEvents

export class EntryShared {
    constructor() {
        this.initializeSentry()
        globalClientVariables = new GlobalClientVariables()
        void registerServiceWorker()
        EntryShared.sendPageVisitAnalyticEvent()
    }

    async initializeSoundAndVersionController() {
        UIImagePathController.setVersionNumber(UIUtilsEjsVariables.versionNumber)

        EntryShared.initializeAudio()

        await CA.initLogRocket()
        CA.initializeSessionRecorder()

        versionController = new UIVersionController(StorageWrapper.getVersionNo, UIUtilsEjsVariables.versionNumber, StorageWrapper.setVersionNo)
    }

    webGLCheck() {
        UIDocument.webGLCheck()
    }

    initializeGameManager(clientSpecificGameManager: UIGameManager) {
        uiGameManager = clientSpecificGameManager
    }

    initializeUIManagerEvents(clientSpecificUIManagerEventHandler: UIManagerEvents) {
        setUIManagerEvents(clientSpecificUIManagerEventHandler)
    }

    static initializeSocket(socketActionController: SocketActionController, isGameActive?: (() => boolean) | undefined) {
        socket = new Socket(socketActionController)

        // bad place for this, but I could not find a better location. the uiGameManager should register itself, but it doesn't know if there is an active game
        socket.controller.registerSessionEstablishedObserver(() => {
            if(isGameActive?.()) {
                uiGameManager.socketGameSend.notifyBackendClientAutoReconnected()
            }
        })

        socket.controller.registerSessionEstablishedObserver(() => {
            notificationController?.removeSocketErrorBanners()
        })
    }

    static initializeNotification(notification: UINotificationController) {
        notificationController = notification
    }

    completeInitialization() {
        void UIServerNotificationController.displayAllPendingNotifications()
        void UIGameSpriteHandler.loadImages()
        uiGameManager.initializeSocket(socket.gameListener.gameState)

        UIDocument.debugOptions()
        // UIDocument.showTestServerPopup()

        UINotificationSendHelper.init()

        UIReferralController.init()
    }

    static async initializeEntry() {
        await ClientI18nInitiation.init()

        const userStateLoaded = await ClientUserState.init()
        if(!userStateLoaded) {
            const errorPopup = new ViewPopup({key: 'strings:popups.errors.initializationError.title'},
                {key: 'strings:popups.errors.initializationError.initializationFailed'},
                () => window.location.reload(),
                () => undefined,
                false, {
                    showXButton: false,
                    showXonHeader: false,
                })
            errorPopup.show()
            const nearlyForever = 2_147_483_647
            await delay(nearlyForever) // TODO fix initialization so it can be stopped without this workaround
        }

        ClientPlatformRedirection.handleAccountRedirection()
        ClientPlatformRedirection.handlePortraitModeEnforcement()

        ClientUserState.registerChangeListener(() => {
            GoogleOneTapLoader.init()
            HotjarLoader.init()
            ValueImpressionLoader.init()
            AdinPlayLoader.init()
            this.eagerlyObtainTurnstilePreClearance()
        })

        ColonistMembershipProduct.initProducts(UIUtilsEjsVariables.discordSKUIds)
        ColonistGiftMembershipProduct.initProducts(UIUtilsEjsVariables.discordSKUIds)
        ColonistCoinProduct.initProducts(UIUtilsEjsVariables.discordSKUIds)
        ColonistOneTimePurchaseProduct.initProducts(UIUtilsEjsVariables.discordSKUIds)

        void FeedbackFormUtils.fetchShouldShowFeedbackForm()

        tabCommunication = TabCommunication.createTabCommunication()
        tabCommunication.onEvent(TabCommunicationEvent.ReloadWebsite, () => window.location.reload())

        initTooltips()
    }

    private initializeSentry() {
        const isLocalhost = window.location.hostname === 'localhost'
        const dsn = UIUtilsEjsVariables.isUserOnMobileAppOrWeb ? UIUtilsEjsVariables.mobileAppSentryDSN : UIUtilsEjsVariables.frontendSentryDSN
        if(isLocalhost || !dsn) {
            window.onunhandledrejection = (event: PromiseRejectionEvent) => {
                if(event.reason instanceof AbortError) {
                    event.preventDefault() // don't log AbortErrors
                }
            }
            return
        }

        // eslint-disable-next-line @typescript-eslint/typedef
        const filterTransactionSpans: NonNullable<BrowserOptions['beforeSendTransaction']> = function(event) {
            if(event.spans == undefined) return null

            const FIFTY_MS = 0.05
            const TWO_FIFTY_MS = 0.250

            event.spans = event.spans.filter((span) => {
                if(span.timestamp == undefined) return true
                // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
                switch(span.origin) {
                    case 'auto.ui.browser.metrics':
                        return (span.timestamp - span.start_timestamp) > FIFTY_MS
                    case 'auto.resource.browser.metrics':
                        return (span.timestamp - span.start_timestamp) > TWO_FIFTY_MS
                }
                return true
            })

            return event
        }

        Sentry.init({
            dsn: dsn,
            release: `Colonist:v${UIUtilsEjsVariables.versionNumber}`,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                }),
            ],
            tracesSampleRate: UIUtilsEjsVariables.frontendSentryTraceSampleRate,
            replaysSessionSampleRate: 0.0005,
            replaysOnErrorSampleRate: 0.0005,
            allowUrls: [
                window.location.hostname,
            ],
            environment: UIUtilsEjsVariables.frontendSentryEnvironment,
            ignoreErrors: [/^AbortError$/],
            beforeSend: (event, hint) => {
                const errorMessage = event.message || event.exception?.values?.[0]?.value
                if(errorMessage === 'Failed to start the audio device') {
                    return null
                }

                event.user ??= {}
                if(ClientUserState.userState?.id) event.user.id = ClientUserState.userState.id
                if(ClientUserState.userState?.username) event.user.username = ClientUserState.userState.username

                return event
            },
            beforeSendTransaction: filterTransactionSpans,
        })
    }

    static sendPageVisitAnalyticEvent() {
        const pathName = UIUrlController.getPathnameWithoutLanguagePrefix()
        const event: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.PageVisit,
            category: AnalyticEventCategory.PageVisit,
            stringValue: pathName,
        }
        void CA.sendAnalyticEvent(event)
    }

    private static eagerlyObtainTurnstilePreClearance() {
        if(UIUtilsEjsVariables.isUserOnDiscord) return // doesn't support/require turnstile

        const userWillBeCreatedForActions = (ClientUserState.userState == undefined) && !ClientUserState.isStale()
        if(!userWillBeCreatedForActions) return

        void UITurnstile.obtainPreClearanceCookie()
    }

    static initializeAudio() {
        const volume = StorageWrapper.getEffectiveVolume()
        void AudioManager.init(volume)
    }
}

export function setUIManagerEvents(handler: UIManagerEvents) {
    uiManagerEvents = handler
}

async function registerServiceWorker() {
    if('serviceWorker' in navigator) {
        try {
            const serviceWorkerLocation = `${UIUtilsEjsVariables.isUserOnDiscord ? '/.proxy/' : '/'}service-worker${UIUtilsEjsVariables.isUserOnMobile ? '?mobile' : ''}`
            await navigator.serviceWorker.register(serviceWorkerLocation)
        } catch(error) {
            logError('registerServiceWorker', [`Service Worker registration failed with error: '${error}'`])
        }
    }
}
